import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Star,
  MapPin,
  Phone,
  Navigation,
  Clock,
  Waves,
  UtensilsCrossed,
  Wifi,
  Sparkles,
  Dumbbell,
  Car,
  ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ResortFaqSection from "@/components/ResortFaqSection";
import { useAuthState } from "@/hooks/useAuthState";
import { DevelopmentService } from "@/services/developmentService";
import {
  DevelopmentFaqService,
  type DevelopmentFaq,
} from "@/services/developmentFaqService";

export type ResortStat = {
  value: string;
  label: string;
};

export type ResortGalleryItem = {
  src: string;
  alt: string;
  span?: "wide" | "tall" | "big";
};

export type ResortFeature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export type ResortContactInfo = {
  address: string;
  cityState?: string;
  phone?: string;
  travelInfo?: string;
  mapEmbedUrl?: string;
  directionsUrl?: string;
};

export type ResortTemplateProps = {
  name: string;
  location: string;
  highlightLabel?: string;
  heroImage: string;
  heroDescription: string;
  rating?: { value: string; reviews: string };
  primaryCtaLabel?: string;
  primaryCtaTo?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  about: {
    eyebrow?: string;
    title: string;
    paragraphs: string[];
    image: string;
    imageAlt?: string;
    stats: ResortStat[];
  };
  gallery?: {
    eyebrow?: string;
    title: string;
    description?: string;
    items: ResortGalleryItem[];
  };
  features?: {
    eyebrow?: string;
    title: string;
    description?: string;
    items: ResortFeature[];
  };
  location_section?: {
    eyebrow?: string;
    title: string;
    description?: string;
    contact: ResortContactInfo;
  };
  /** Carrega FAQ público do empreendimento (admin). */
  developmentId?: number;
  developmentSlug?: string;
};

const DEFAULT_FEATURES: ResortFeature[] = [
  {
    icon: Clock,
    title: "Horário de Funcionamento",
    description: "Recepção 24h e atendimento dedicado para você curtir sem preocupação.",
  },
  {
    icon: Waves,
    title: "Piscinas & Praia",
    description: "Áreas aquáticas completas e fácil acesso às melhores praias da região.",
  },
  {
    icon: UtensilsCrossed,
    title: "Gastronomia",
    description: "Café da manhã regional, restaurantes à la carte e bar para todos os momentos.",
  },
  {
    icon: Wifi,
    title: "Wi-Fi Premium",
    description: "Internet de alta velocidade gratuita em todas as áreas do empreendimento.",
  },
  {
    icon: Sparkles,
    title: "Spa & Bem-estar",
    description: "Massagens, sauna e tratamentos exclusivos em um ambiente relaxante.",
  },
  {
    icon: Dumbbell,
    title: "Academia & Esportes",
    description: "Academia equipada, quadras esportivas e atividades para toda a família.",
  },
  {
    icon: Car,
    title: "Estacionamento",
    description: "Vagas privativas e gratuitas, com manobrista disponível 24h.",
  },
  {
    icon: ShieldCheck,
    title: "Segurança 24h",
    description: "Monitoramento, equipe treinada e acesso controlado para sua tranquilidade.",
  },
];

const galleryClassFor = (span?: ResortGalleryItem["span"]) => {
  switch (span) {
    case "big":
      return "md:col-span-2 md:row-span-2";
    case "wide":
      return "md:col-span-2";
    case "tall":
      return "md:row-span-2";
    default:
      return "";
  }
};

const ResortTemplate = ({
  name,
  location,
  highlightLabel = "Resort em Destaque",
  heroImage,
  heroDescription,
  rating,
  primaryCtaLabel = "Reservar Agora",
  primaryCtaTo,
  secondaryCtaLabel = "Ver Galeria",
  secondaryCtaHref = "#galeria",
  about,
  gallery,
  features,
  location_section,
  developmentId,
  developmentSlug,
}: ResortTemplateProps) => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const [faqItems, setFaqItems] = useState<DevelopmentFaq[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadFaq = async () => {
      try {
        let devId = developmentId;
        if (!devId && developmentSlug) {
          const dev = await DevelopmentService.getDevelopmentBySlug(developmentSlug);
          devId = dev?.id;
        }
        if (!devId) {
          if (!cancelled) setFaqItems([]);
          return;
        }
        const list = await DevelopmentFaqService.listPublic(devId);
        if (!cancelled) {
          setFaqItems(list.filter((f) => f.is_active));
        }
      } catch {
        if (!cancelled) setFaqItems([]);
      }
    };

    loadFaq();
    return () => {
      cancelled = true;
    };
  }, [developmentId, developmentSlug]);

  const featureItems = features?.items ?? DEFAULT_FEATURES;
  const featureEyebrow = features?.eyebrow ?? "Destaques";
  const featureTitle = features?.title ?? "Tudo o que você precisa, em um único lugar";
  const featureDescription =
    features?.description ?? "Comodidades pensadas para que sua estadia seja memorável do check-in ao check-out.";

  const reservationHref =
    primaryCtaTo ?? `/busca?empreendimento=${encodeURIComponent(name)}`;

  return (
    <div className="min-h-screen bg-background">
      <main>
        {/* Hero — banner ocupa o topo da página com header sobreposto */}
        <section className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
          <img
            src={heroImage}
            alt={`${name} - banner principal`}
            className="absolute inset-0 size-full object-cover scale-105 animate-[zoomHero_20s_ease-in-out_infinite_alternate]"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-transparent" />

          {/* Header sobreposto ao banner (estilo Home) */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4 md:pt-5">
            <div className="pointer-events-auto mx-auto max-w-7xl relative [&_header]:sticky [&_header]:top-auto [&_header]:border-b-0 [&_header]:bg-transparent [&_header]:supports-[backdrop-filter]:bg-transparent [&_header]:backdrop-blur-0 [&_.container]:min-h-14 [&_.container]:px-2 sm:[&_.container]:px-4">
              <Navbar
                isAuthenticated={isAuthenticated}
                isOwner={userType === "owner"}
                userName={userName}
                userPhoto={userPhoto}
                userId={userId}
                userType={userType}
                cleanHeader
              />
            </div>
          </div>

          <div className="relative h-full container mx-auto px-4 sm:px-6 flex flex-col justify-end pb-16 sm:pb-24">
            <div className="max-w-3xl space-y-5 animate-[fadeUpHero_0.8s_ease]">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/90 text-accent-foreground text-xs font-semibold uppercase tracking-wider shadow-sm">
                  <Star className="size-3.5 fill-current" />
                  {highlightLabel}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-medium">
                  <MapPin className="size-3.5" />
                  {location}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.18] sm:leading-[1.15]">
                <span className="box-decoration-clone bg-primary/90 text-primary-foreground px-3 sm:px-4 py-1 sm:py-1.5 rounded-md shadow-md">
                  {name}
                </span>
              </h1>
              <p className="max-w-2xl text-base sm:text-xl font-medium leading-[1.7] sm:leading-[1.75]">
                <span className="box-decoration-clone bg-primary/80 text-primary-foreground px-2.5 sm:px-3 py-1 rounded-md">
                  {heroDescription}
                </span>
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to={reservationHref}
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform"
                >
                  {primaryCtaLabel}
                </Link>
                <a
                  href={secondaryCtaHref}
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold hover:bg-white/20 transition-colors"
                >
                  {secondaryCtaLabel}
                </a>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes zoomHero { from { transform: scale(1.05); } to { transform: scale(1.15); } }
            @keyframes fadeUpHero { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
        </section>

        {/* Sobre */}
        <section id="sobre" className="container mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={about.image}
                  alt={about.imageAlt ?? `Vista do resort ${name}`}
                  loading="lazy"
                  className="size-full object-cover"
                />
              </div>
              {rating && (
                <div className="absolute -bottom-6 -right-6 bg-card rounded-2xl p-5 shadow-xl border border-border w-44 hidden sm:block">
                  <div className="text-3xl font-bold text-foreground">
                    {rating.value}
                    <span className="text-accent">★</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{rating.reviews}</div>
                </div>
              )}
            </div>
            <div className="space-y-5">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                {about.eyebrow ?? "Sobre o Resort"}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
                {about.title}
              </h2>
              {about.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className={
                    index === 0
                      ? "text-muted-foreground leading-relaxed text-lg"
                      : "text-muted-foreground leading-relaxed"
                  }
                >
                  {paragraph}
                </p>
              ))}
              {about.stats.length > 0 && (
                <div className="grid grid-cols-3 gap-4 pt-4">
                  {about.stats.map((stat) => (
                    <div
                      key={`${stat.value}-${stat.label}`}
                      className="rounded-2xl bg-secondary/15 p-4 text-center"
                    >
                      <div className="text-2xl font-bold text-primary">{stat.value}</div>
                      <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Galeria */}
        {gallery && gallery.items.length > 0 && (
          <section id="galeria" className="container mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-20 sm:pb-28">
            <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-3">
                  {gallery.eyebrow ?? "Galeria"}
                </p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  {gallery.title}
                </h2>
              </div>
              {gallery.description && (
                <p className="text-muted-foreground max-w-md text-base leading-relaxed">
                  {gallery.description}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-3 sm:gap-4">
              {gallery.items.map((item, index) => (
                <figure
                  key={`${item.src}-${index}`}
                  className={`group relative overflow-hidden rounded-2xl shadow-md ${galleryClassFor(item.span)}`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <figcaption className="absolute bottom-3 left-4 right-4 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-300">
                    {item.alt}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Destaques */}
        <section id="destaques" className="bg-secondary/10 py-20 sm:py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-3">
                {featureEyebrow}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {featureTitle}
              </h2>
              <p className="text-muted-foreground mt-4 text-lg">{featureDescription}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {featureItems.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="group relative bg-card rounded-2xl p-6 border border-border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="size-12 rounded-xl bg-gradient-to-br from-primary to-secondary grid place-items-center mb-4 shadow-md group-hover:scale-110 transition-transform">
                      <Icon className="size-6 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Localização */}
        {location_section && (
          <section
            id="localizacao"
            className="container mx-auto px-4 sm:px-6 py-20 sm:py-28"
          >
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-3">
                {location_section.eyebrow ?? "Localização"}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {location_section.title}
              </h2>
              {location_section.description && (
                <p className="text-muted-foreground mt-4 text-lg">{location_section.description}</p>
              )}
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-3xl overflow-hidden shadow-xl border border-border bg-card aspect-[16/10] lg:aspect-auto lg:min-h-[460px]">
                {location_section.contact.mapEmbedUrl ? (
                  <iframe
                    title={`Mapa ${name}`}
                    src={location_section.contact.mapEmbedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-muted-foreground p-6 text-center">
                    Mapa não disponível para este empreendimento.
                  </div>
                )}
              </div>
              <div
                className="rounded-3xl p-8 flex flex-col justify-between shadow-xl text-white relative overflow-hidden bg-gradient-to-br from-primary to-secondary"
              >
                <div className="absolute -top-20 -right-20 size-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
                <div className="space-y-6">
                  <div className="size-14 rounded-2xl bg-white/20 backdrop-blur grid place-items-center ring-1 ring-white/30">
                    <MapPin className="size-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold leading-tight text-white">{name}</h3>
                    <p className="mt-2 leading-relaxed text-white/90">
                      {location_section.contact.address}
                    </p>
                    {location_section.contact.cityState && (
                      <p className="mt-1 text-white/80 text-sm">
                        {location_section.contact.cityState}
                      </p>
                    )}
                  </div>
                  <div className="space-y-3 pt-2 border-t border-white/15">
                    {location_section.contact.travelInfo && (
                      <div className="flex items-start gap-3">
                        <Navigation className="size-5 mt-0.5 shrink-0" />
                        <div className="text-sm">
                          <div className="font-semibold text-white">Como chegar</div>
                          <div className="text-white/80">
                            {location_section.contact.travelInfo}
                          </div>
                        </div>
                      </div>
                    )}
                    {location_section.contact.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="size-5 mt-0.5 shrink-0" />
                        <div className="text-sm">
                          <div className="font-semibold text-white">Recepção 24h</div>
                          <div className="text-white/80">{location_section.contact.phone}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {location_section.contact.directionsUrl && (
                  <a
                    href={location_section.contact.directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="relative mt-6 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white text-primary font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform shadow-lg"
                  >
                    <Navigation className="size-4" />
                    Traçar rota no Google Maps
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Call to action */}
        <section className="container mx-auto px-4 sm:px-6 pb-24">
          <div className="rounded-3xl bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 px-6 sm:px-12 py-14 text-center max-w-5xl mx-auto border border-border shadow-sm">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
              Pronto para viver essa experiência?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Consulte disponibilidade e descubra as melhores condições para a sua estadia em
              {" "}
              <span className="font-semibold text-foreground">{name}</span>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={reservationHref}
                className="inline-flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8 transition-colors"
              >
                Ver disponibilidade
              </Link>
              <Link
                to="/fale-conosco"
                className="inline-flex items-center justify-center rounded-full border border-input bg-background hover:bg-accent hover:text-accent-foreground font-semibold h-12 px-8 transition-colors"
              >
                Fale Conosco
              </Link>
            </div>
          </div>
        </section>
      </main>

      <ResortFaqSection resortName={name} items={faqItems} />

      <Footer />
    </div>
  );
};

export default ResortTemplate;
